'use client';

import { cn } from '@cobliteam/ui/utils';
import { motion } from 'motion/react';
import { type CSSProperties, type ElementType, type JSX, memo, useMemo } from 'react';

export type TextShimmerProps = {
  children: string;
  as?: ElementType;
  className?: string;
  duration?: number;
  spread?: number;
};

const ShimmerComponent = ({ children, as: Component = 'p', className, duration = 2, spread = 2 }: TextShimmerProps) => {
  const MotionComponent = motion.create(Component as keyof JSX.IntrinsicElements);

  const dynamicSpread = useMemo(() => (children?.length ?? 0) * spread, [children, spread]);
  const shimmerGradient = useMemo(
    () => `linear-gradient(90deg, transparent calc(50% - ${dynamicSpread}px), var(--gray-11) 50%, transparent calc(50% + ${dynamicSpread}px))`,
    [dynamicSpread]
  );

  return (
    <MotionComponent
      animate={{ backgroundPosition: '0% center' }}
      className={cn('relative inline-block bg-clip-text text-transparent', 'bg-[length:250%_100%,auto] bg-[repeat:no-repeat,padding-box]', className)}
      initial={{ backgroundPosition: '100% center' }}
      style={
        {
          '--spread': `${dynamicSpread}px`,
          backgroundImage: `${shimmerGradient}, linear-gradient(var(--gray-11), var(--gray-4))`,
        } as CSSProperties
      }
      transition={{
        repeat: Number.POSITIVE_INFINITY,
        duration,
        ease: 'linear',
      }}
    >
      {children}
    </MotionComponent>
  );
};

export const Shimmer = memo(ShimmerComponent);
